import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useInterviewStart } from './context/InterviewContext';
import { baseUrl, Base_Url_Landing_Page_Backend, Base_Url_Landing_Page_Frontend } from './utils/baseUri';
import API from './utils/api';
import { Sparkles, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const App = lazy(() => import('./App'));
const ThankyouPage = lazy(() => import('./components/ThankyouPage'));

const Main = () => {
  const [verifyUserLoading, setVerifyUserLoading] = useState(false); // Set initial state to true
  const { setUser } = useInterviewStart();

  useEffect(() => {
    // Get the session ticket or encrypted token from URL parameters
    const params = new URLSearchParams(window.location.search);
    const ticket = params.get('ticket');
    const encryptedToken = params.get('user'); // Legacy parameter fallback

    if (ticket || encryptedToken) {
      setVerifyUserLoading(true);

      // Clean the ticket/token from URL bar immediately for security
      window.history.replaceState({}, document.title, window.location.pathname);

      API
        .get(`${baseUrl}/api/verify`, {
          params: ticket ? { ticket } : { token: encryptedToken },
        })
        .then((response) => {
          const user = response.data?.user;
          const userId = user?.id || user?._id;

          // Save user to sessionStorage (and clear any legacy localStorage)
          sessionStorage.setItem("interview-user", JSON.stringify(user));
          localStorage.removeItem("interview-user");

          // Set the verified user in context and storage
          setUser(user);
          setVerifyUserLoading(false);
          toast.success(`Welcome ${user?.fullName || "Candidate"}`, {
            position: "top-right",
            duration: 3000,
          });
        })
        .catch((error) => {
          console.error('Session verification failed:', error);
          toast.error('Session verification failed. Please login again.', {
            position: "top-right",
            duration: 3000,
          });
          setVerifyUserLoading(false);
          window.location.href = `${Base_Url_Landing_Page_Frontend}/login`;
        });
    } else {
      // Check if there is already an existing user in sessionStorage with valid active session
      const existingUser = sessionStorage.getItem("interview-user") || localStorage.getItem("interview-user");
      if (existingUser) {
        try {
          const parsed = JSON.parse(existingUser);
          sessionStorage.setItem("interview-user", existingUser);
          localStorage.removeItem("interview-user");
          setUser(parsed);
          setVerifyUserLoading(false);
          return;
        } catch (e) {}
      }

      // No ticket or existing session found, redirect to login
      toast.error('Please login again.', {
        position: "top-right",
        duration: 3000,
      });
      setVerifyUserLoading(false);
      window.location.href = `${Base_Url_Landing_Page_Frontend}/login`;
    }
  }, [setUser]);


  return (
    <div className="bg-[#020617] min-h-screen">
      <AnimatePresence>
        {verifyUserLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-[#020617] z-50 overflow-hidden"
          >
            {/* Ambient background glows */}
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-amber-500/10 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-rose-500/5 rounded-full blur-[120px]"></div>

            <div className="relative text-center px-10">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col items-center"
              >
                {/* Logo & Icon Container */}
                <div className="relative mb-12">
                  <div className="absolute inset-0 bg-amber-500/20 blur-2xl rounded-full"></div>
                  <div className="relative w-24 h-24 bg-slate-900 border border-white/10 rounded-[30px] flex items-center justify-center shadow-2xl">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-2 border-2 border-dashed border-amber-500/30 rounded-[24px]"
                    ></motion.div>
                    <ShieldCheck className="w-10 h-10 text-amber-500" />
                  </div>
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute -top-2 -right-2 bg-amber-500 rounded-full p-2 shadow-lg"
                  >
                    <Sparkles className="w-4 h-4 text-black" />
                  </motion.div>
                </div>

                {/* Progress Indicator */}
                <div className="w-64 h-1.5 bg-slate-800 rounded-full mb-8 overflow-hidden border border-white/5">
                  <motion.div
                    initial={{ x: "-100%" }}
                    animate={{ x: "100%" }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                    className="w-full h-full bg-gradient-to-r from-transparent via-amber-500 to-transparent"
                  ></motion.div>
                </div>

                <h2 className="text-3xl font-black text-white tracking-tight mb-4">
                  Authenticating <span className="text-amber-500">Session</span>
                </h2>

                <div className="flex flex-col gap-2">
                  <p className="text-slate-400 font-medium tracking-wide">
                    EZSync is verifying your credentials...
                  </p>
                  <p className="text-slate-500 text-sm font-medium italic">
                    "Crafting tailored interview questions just for you"
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Subtle branding at bottom */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-2 opacity-80">
              <img src="/logo.png" alt="EZSync" className="h-6 brightness-0 invert" />
              <div className="h-4 w-[1px] bg-white/20"></div>
              <span className="text-white text-xs font-bold tracking-widest uppercase">Secure Interface</span>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Suspense fallback={
              <div className="flex min-h-screen items-center justify-center bg-[#020617]">
                <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            }>
              <Routes>
                <Route path="/" element={<App />} />
                <Route path="/interview-completed" element={<ThankyouPage />} />
              </Routes>
            </Suspense>
            <Toaster
              position="top-right"
              reverseOrder={false}
              toastOptions={{
                duration: 3000,
                style: { background: '#333', color: '#fff' },
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Main;
